const CHAT_RENDER_DEBUG_DEFAULT = false;
const CHAT_RENDER_DEBUG_STORAGE_KEY = 'lb:debug:chat-render';
const MAX_RETAINED_LOGS = 500;

interface ChatRenderLogEntry {
  tag: string;
  data: Record<string, unknown>;
  time: number;
}

interface ChatRenderDebugApi {
  disable(): void;
  enable(): void;
  getLogs(): ChatRenderLogEntry[];
  getCounts(): Record<string, number>;
  isEnabled(): boolean;
  reset(): void;
}

declare global {
  interface Window {
    __LB_CHAT_RENDER_DEBUG__?: boolean;
    __lbChatRenderDebug?: ChatRenderDebugApi;
  }
}

const counts = new Map<string, number>();
const logs: ChatRenderLogEntry[] = [];

function readStoredDebugFlag() {
  try {
    return (
      window.localStorage.getItem(CHAT_RENDER_DEBUG_STORAGE_KEY) === 'true'
    );
  } catch {
    return false;
  }
}

export function isChatRenderDebugEnabled() {
  if (!import.meta.env.DEV || typeof window === 'undefined') return false;

  return (
    CHAT_RENDER_DEBUG_DEFAULT ||
    window.__LB_CHAT_RENDER_DEBUG__ === true ||
    readStoredDebugFlag()
  );
}

export function logChatRenderDebug(
  tag: string,
  data: Record<string, unknown> = {},
  countKey = 'global'
) {
  if (!isChatRenderDebugEnabled()) return 0;

  const key = `${tag}:${countKey}`;
  const count = (counts.get(key) ?? 0) + 1;
  counts.set(key, count);

  const payload = { count, ...data };
  logs.push({ tag, data: payload, time: performance.now() });
  if (logs.length > MAX_RETAINED_LOGS) logs.shift();

  console.info(`[${tag}]`, payload);
  return count;
}

function resetChatRenderDebug() {
  counts.clear();
  logs.length = 0;
}

if (import.meta.env.DEV && typeof window !== 'undefined') {
  window.__lbChatRenderDebug = {
    disable() {
      window.__LB_CHAT_RENDER_DEBUG__ = false;
      window.localStorage.removeItem(CHAT_RENDER_DEBUG_STORAGE_KEY);
    },
    enable() {
      window.__LB_CHAT_RENDER_DEBUG__ = true;
      window.localStorage.setItem(CHAT_RENDER_DEBUG_STORAGE_KEY, 'true');
    },
    getLogs() {
      return [...logs];
    },
    getCounts() {
      return Object.fromEntries(counts);
    },
    isEnabled: isChatRenderDebugEnabled,
    reset: resetChatRenderDebug,
  };
}
